import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getPublicMenu, getPublicBusiness, createOrder } from '../api';

export default function MenuPage() {
  const { businessId } = useParams();
  const navigate       = useNavigate();

  const [shop,         setShop]        = useState(null);
  const [menuItems,    setMenuItems]   = useState([]);
  const [loading,      setLoading]     = useState(true);
  const [cart,         setCart]        = useState({});
  const [placing,      setPlacing]     = useState(false);
  const [error,        setError]       = useState('');
  const [notes,        setNotes]       = useState('');
  const [search,       setSearch]      = useState('');
  const [orderType,    setOrderType]   = useState('dine-in');
  const [typeReady,   setTypeReady]   = useState(false);

  // Customer info state
  const ciKey = 'ci_' + businessId;
  const [customerInfo, setCustomerInfo] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(ciKey)); } catch { return null; }
  });

  const urlTable = (() => {
    try { return new URLSearchParams(window.location.search).get('table') || ''; } catch { return ''; }
  })();
  const [infoForm, setInfoForm] = useState({ name: '', table: urlTable, email: '' });
  const [infoErr,  setInfoErr]  = useState('');

  useEffect(() => {
    Promise.all([
      getPublicBusiness(businessId),
      getPublicMenu(businessId),
    ])
      .then(([biz, items]) => {
        setShop(biz);
        setMenuItems(items);
        if (biz.business_type === 'store') setOrderType('delivery');
        setTypeReady(true);
      })
      .catch(() => setError('Shop not found or menu unavailable.'))
      .finally(() => setLoading(false));
  }, [businessId]);

  const qty    = (id) => cart[id] || 0;
  const add    = (item) => setCart((p) => ({ ...p, [item.id]: (p[item.id] || 0) + 1 }));
  const remove = (item) => setCart((p) => {
    const n = { ...p };
    if (n[item.id] > 1) n[item.id]--;
    else delete n[item.id];
    return n;
  });

  const totalItems  = Object.values(cart).reduce((s, n) => s + n, 0);
  const totalAmount = menuItems.reduce((s, i) => s + i.price * (cart[i.id] || 0), 0);

  const submitInfo = () => {
    if (!infoForm.name.trim()) { setInfoErr('Please enter your name.'); return; }
    const ci = { name: infoForm.name.trim(), table: infoForm.table.trim(), email: infoForm.email.trim() };
    sessionStorage.setItem(ciKey, JSON.stringify(ci));
    setCustomerInfo(ci);
    setInfoErr('');
  };

  const placeOrder = async () => {
    if (!totalItems) return;
    setPlacing(true); setError('');
    try {
      const items = menuItems
        .filter((i) => cart[i.id])
        .map((i) => ({ id: i.id, name: i.name, price: i.price, quantity: cart[i.id] }));
      const order = await createOrder(
        businessId, items, totalAmount,
        customerInfo?.name || 'Guest',
        customerInfo?.table || '',
        notes,
        orderType,
        customerInfo?.email || '',
      );
      navigate(`/shop/${businessId}/order/${order.id}`);
    } catch {
      setError('Could not place order. Please try again.');
      setPlacing(false);
    }
  };

  if (loading) return (
    <div className="page">
      <div className="header"><h1>Scanningo.in</h1></div>
      <div className="loading">Loading menu…</div>
    </div>
  );

  if (error && !menuItems.length) return (
    <div className="page">
      <div className="header"><h1>Scanningo.in</h1></div>
      <div className="loading" style={{ color:'#c0392b' }}>{error}</div>
    </div>
  );

  // Filter menu items for search
  const searchLower = search.toLowerCase();
  const visibleItems = search
    ? menuItems.filter((i) =>
        i.name.toLowerCase().includes(searchLower) ||
        (i.description || '').toLowerCase().includes(searchLower)
      )
    : menuItems;

  const categories = [...new Set(visibleItems.map((i) => i.category))];

  return (
    <div className="page">
      {/* ── Customer info overlay ── */}
      {!customerInfo && !loading && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '32px 28px',
            width: '100%', maxWidth: 360, boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
          }}>
            <div style={{textAlign:'center',fontWeight:700,fontSize:20,marginBottom:6}}>
              {shop?.name || 'Welcome'}
            </div>
            <div style={{textAlign:'center',fontSize:13,color:'#666',marginBottom:24}}>
              Tell us who you are to place an order
            </div>
            {infoErr && (
              <div style={{color:'#c0392b',fontSize:13,marginBottom:10}}>{infoErr}</div>
            )}
            <div style={{marginBottom:14}}>
              <label style={{fontSize:13,fontWeight:600,display:'block',marginBottom:4}}>
                Your Name *
              </label>
              <input
                style={{width:'100%',padding:'10px 12px',border:'1px solid #d1d5db',
                        borderRadius:8,fontSize:15,outline:'none'}}
                placeholder="e.g. Rahul"
                value={infoForm.name}
                onChange={(e) => setInfoForm((p) => ({...p, name: e.target.value}))}
                onKeyDown={(e) => { if (e.key === 'Enter') submitInfo(); }}
                autoFocus
              />
            </div>
            <div style={{marginBottom:14}}>
              <label style={{fontSize:13,fontWeight:600,display:'block',marginBottom:4}}>
                Email <span style={{fontWeight:400,color:'#888'}}>(optional — to receive bill)</span>
              </label>
              <input
                type="email"
                style={{width:'100%',padding:'10px 12px',border:'1px solid #d1d5db',
                        borderRadius:8,fontSize:15,outline:'none'}}
                placeholder="e.g. rahul@gmail.com"
                value={infoForm.email}
                onChange={(e) => setInfoForm((p) => ({...p, email: e.target.value}))}
                onKeyDown={(e) => { if (e.key === 'Enter') submitInfo(); }}
              />
            </div>
            {(() => {
              const isStore = shop?.business_type === 'store';
              if (isStore && orderType === 'delivery') {
                return (
                  <div style={{marginBottom:24}}>
                    <label style={{fontSize:13,fontWeight:600,display:'block',marginBottom:4}}>
                      Delivery Address <span style={{fontWeight:400,color:'#888'}}>(optional)</span>
                    </label>
                    <input
                      style={{width:'100%',padding:'10px 12px',border:'1px solid #d1d5db',
                              borderRadius:8,fontSize:15,outline:'none'}}
                      placeholder="e.g. House 12, Sector 5, Noida"
                      value={infoForm.table}
                      onChange={(e) => setInfoForm((p) => ({...p, table: e.target.value}))}
                      onKeyDown={(e) => { if (e.key === 'Enter') submitInfo(); }}
                    />
                  </div>
                );
              }
              if (isStore && orderType === 'pickup') {
                return (
                  <div style={{marginBottom:24}}>
                    <label style={{fontSize:13,fontWeight:600,display:'block',marginBottom:4}}>
                      Phone / Token <span style={{fontWeight:400,color:'#888'}}>(optional)</span>
                    </label>
                    <input
                      style={{width:'100%',padding:'10px 12px',border:'1px solid #d1d5db',
                              borderRadius:8,fontSize:15,outline:'none'}}
                      placeholder="e.g. 9876543210 or Token 3"
                      value={infoForm.table}
                      onChange={(e) => setInfoForm((p) => ({...p, table: e.target.value}))}
                      onKeyDown={(e) => { if (e.key === 'Enter') submitInfo(); }}
                    />
                  </div>
                );
              }
              if (orderType !== 'delivery') {
                return (
                  <div style={{marginBottom:24}}>
                    <label style={{fontSize:13,fontWeight:600,display:'block',marginBottom:4}}>
                      {orderType === 'takeaway'
                        ? <>Token / Name <span style={{fontWeight:400,color:'#888'}}>(optional)</span></>
                        : <>Table Number <span style={{fontWeight:400,color:'#888'}}>(optional)</span></>
                      }
                    </label>
                    <input
                      style={{width:'100%',padding:'10px 12px',border:'1px solid #d1d5db',
                              borderRadius:8,fontSize:15,outline:'none'}}
                      placeholder={orderType === 'takeaway' ? 'e.g. Token 5' : 'e.g. T-3 or Counter'}
                      value={infoForm.table}
                      onChange={(e) => setInfoForm((p) => ({...p, table: e.target.value}))}
                      onKeyDown={(e) => { if (e.key === 'Enter') submitInfo(); }}
                    />
                  </div>
                );
              }
              return null;
            })()}
            <button
              onClick={submitInfo}
              style={{width:'100%',padding:'12px',background:'#FF6B35',color:'#fff',
                      border:'none',borderRadius:10,fontSize:16,fontWeight:700,cursor:'pointer'}}>
              Continue →
            </button>
          </div>
        </div>
      )}

      <div className="header">
        <h1>{shop?.name || 'Scanningo.in'}</h1>
        <p>{shop?.description || 'Select items and place your order'}</p>
      </div>

      {typeReady && (() => {
        const isStore = shop?.business_type === 'store';
        const orderTypes = isStore
          ? [['pickup','Pick Up'],['delivery','Home Delivery']]
          : [['dine-in','Dine In'],['takeaway','Take Away'],['delivery','Delivery']];
        return (
          <div style={{display:'flex',gap:8,padding:'12px 16px 4px',justifyContent:'center'}}>
            {orderTypes.map(([val, label]) => (
              <button
                key={val}
                onClick={() => setOrderType(val)}
                style={{
                  padding:'7px 16px',borderRadius:20,fontSize:13,fontWeight:600,
                  border:`2px solid ${orderType === val ? '#FF6B35' : '#e5e7eb'}`,
                  background: orderType === val ? '#FF6B35' : '#fff',
                  color: orderType === val ? '#fff' : '#555',
                  cursor:'pointer',transition:'all 0.15s',
                }}
              >{label}</button>
            ))}
          </div>
        );
      })()}

      <div className="menu-content">
        {/* Search bar */}
        {menuItems.length > 5 && (
          <div style={{padding:'8px 0 4px'}}>
            <input
              placeholder="Search menu..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{width:'100%',padding:'10px 14px',border:'1px solid #e5e7eb',
                      borderRadius:10,fontSize:14,outline:'none',
                      background:'#f9fafb',boxSizing:'border-box'}}
            />
          </div>
        )}

        {visibleItems.length === 0 && search && (
          <div style={{textAlign:'center',color:'#888',padding:'24px 0',fontSize:14}}>
            No items match "{search}"
          </div>
        )}

        {categories.map((cat) => (
          <div key={cat} className="category-section">
            <div className="category-title">{cat}</div>
            {visibleItems.filter((i) => i.category === cat).map((item) => {
              const q = qty(item.id);
              const soldOut = !item.active;
              return (
                <div
                  key={item.id}
                  className="menu-item"
                  style={soldOut ? {opacity:0.55} : {}}
                >
                  <div className="item-info">
                    {item.bestseller && (
                      <span style={{
                        display:'inline-block',background:'#FF6B35',color:'#fff',
                        fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:20,
                        marginBottom:4,letterSpacing:.3,
                      }}>
                        ⭐ Best Seller
                      </span>
                    )}
                    <div className="item-name">{item.name}</div>
                    {item.description && <div className="item-desc">{item.description}</div>}
                    <div className="item-price">₹{item.price}</div>
                    {soldOut && (
                      <div style={{fontSize:11,color:'#ef4444',fontWeight:600,marginTop:2}}>
                        Sold Out
                      </div>
                    )}
                  </div>
                  <div className="qty-control">
                    {soldOut ? (
                      <button
                        className="qty-btn filled"
                        disabled
                        style={{cursor:'not-allowed',opacity:0.5}}
                      >+</button>
                    ) : q === 0 ? (
                      <button className="qty-btn filled" onClick={() => add(item)}>+</button>
                    ) : (
                      <>
                        <button className="qty-btn" onClick={() => remove(item)}>−</button>
                        <span className="qty-count">{q}</span>
                        <button className="qty-btn filled" onClick={() => add(item)}>+</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {totalItems > 0 && (
        <>
          <div style={{padding:'8px 16px 0'}}>
            <textarea
              placeholder="Special instructions (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              style={{width:'100%',padding:'10px 12px',border:'1px solid #e5e7eb',
                      borderRadius:10,fontSize:13,resize:'none',outline:'none',
                      boxSizing:'border-box',color:'#333'}}
            />
          </div>
          <div className="cart-footer">
            <div className="cart-summary">
              <span className="cart-count">{totalItems} item{totalItems > 1 ? 's' : ''}</span>
              <span className="cart-total">₹{totalAmount}</span>
            </div>
            <button className="place-order-btn" onClick={placeOrder} disabled={placing}>
              {placing ? 'Placing Order…' : 'Place Order  →'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
